import DownloadIcon from '@elementor/icons/DownloadIcon';
import HelpIcon from '@elementor/icons/HelpIcon';
import Box from '@elementor/ui/Box';
import Button from '@elementor/ui/Button';
import DialogActions from '@elementor/ui/DialogActions';
import DialogContent from '@elementor/ui/DialogContent';
import DialogContentText from '@elementor/ui/DialogContentText';
import Divider from '@elementor/ui/Divider';
import Stack from '@elementor/ui/Stack';
import Typography from '@elementor/ui/Typography';
import { styled } from '@elementor/ui/styles';
import { useState } from 'react';
import {
	ALLY_LANDING_URL,
	ALLY_REPO_URL,
	INSTALL_MODAL_STEPS,
} from '@constants';
import { useInstallModalContext } from '@context/install-modal-context';
import { useAnalytics } from '@hooks/use-analytics';
import GoogleAnalyticsEvent, {
	EVENT_SECTIONS,
	EVENT_TYPES,
} from '@hooks/use-analytics/google-analytics-event';
import { WordPressLogoIcon } from '@images/wordpress-logo-icon';
import { __ } from '@utils/i18n';
import { injectTemplateVars } from '@utils/inject-template-vars';

const GetFreeModalStepInstall = () => {
	const { sendEvent } = useAnalytics();
	const { close, goToStep } = useInstallModalContext();
	const [videoReady, setVideoReady] = useState(false);
	const YT_ID = 'UDI-WZN0l0E';
	const youtubeSource = `https://www.youtube.com/embed/${YT_ID}?autoplay=1&loop=1&mute=1&playlist=${YT_ID}&modestbranding=1`;
	const youtubeThumbnail = `https://i.ytimg.com/vi_webp/${YT_ID}/sddefault.webp`;

	const onInstallLinkClick = () => {
		close();

		sendEvent(
			new GoogleAnalyticsEvent()
				.setType(EVENT_TYPES.UI)
				.setSection(EVENT_SECTIONS.INSTALL_MODAL)
				.setElementName('install on your wp button')
				.setOutcome('proceeding to wordpress.com')
				.setEnglishText('Install on your WordPress website'),
		);
	};

	const onLearnMoreClick = () => {
		sendEvent(
			new GoogleAnalyticsEvent()
				.setType(EVENT_TYPES.UI)
				.setSection(EVENT_SECTIONS.INSTALL_MODAL)
				.setElementName('learn more button')
				.setOutcome('proceeding to the ally product page')
				.setEnglishText('Learn more'),
		);
	};

	const onDownloadLinkClick = () => {
		sendEvent(
			new GoogleAnalyticsEvent()
				.setType(EVENT_TYPES.UI)
				.setSection(EVENT_SECTIONS.INSTALL_MODAL)
				.setElementName('download now button')
				.setOutcome('downloading the zip')
				.setEnglishText('Download Zip'),
		);
	};

	return (
		<>
			<StyledDialogContent dividers>
				<StyledVideoBox>
					<iframe
						width="550"
						height="300"
						src={youtubeSource}
						title="YouTube video player"
						allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
						referrerPolicy="strict-origin-when-cross-origin"
						onLoad={() => setTimeout(() => setVideoReady(true), 500)}
						style={{ visibility: videoReady ? 'visible' : 'hidden' }}
						allowFullScreen
					></iframe>
					{!videoReady && (
						<StyledPosterOverlay>
							<img src={youtubeThumbnail} alt="" aria-hidden="true" />
						</StyledPosterOverlay>
					)}
				</StyledVideoBox>

				<StyledContentContainer>
					<StyledTitle as="h2" id="ea11y-install-dialog-title">
						{injectTemplateVars(
							__('Make your site accessible {{s}}in minutes{{/s}}'),
							{
								s: ({ children }) => <strong>{children}</strong>,
							},
						)}
					</StyledTitle>

					<StyledSubtitle as="h3" id="ea11y-install-dialog-subtitle">
						{__('Detect and fix issues automatically, no coding required')}
					</StyledSubtitle>

					<StyledRepoButton
						href={ALLY_REPO_URL}
						target="_blank"
						variant="contained"
						color="info"
						onClick={onInstallLinkClick}
					>
						<WordPressLogoIcon />

						{__('Install on your WordPress website')}
					</StyledRepoButton>

					<StyledNotOnWordPressText as="p" variant="body2">
						{__('Not on Wordpress?')}

						<Button
							size="small"
							color="info"
							variant="text"
							onClick={() => goToStep(INSTALL_MODAL_STEPS.SUBSCRIPTION_FORM)}
						>
							{__('Click here')}
						</Button>
					</StyledNotOnWordPressText>
				</StyledContentContainer>
			</StyledDialogContent>

			<StyledDialogActions>
				<Stack
					direction="row"
					divider={<Divider orientation="vertical" variant="middle" flexItem />}
					gap={3}
				>
					<Button
						size="small"
						color="secondary"
						variant="text"
						href="https://downloads.wordpress.org/plugin/pojo-accessibility.latest-stable.zip"
						onClick={onDownloadLinkClick}
						startIcon={<DownloadIcon />}
					>
						{__('Download Zip')}
					</Button>

					<Button
						size="small"
						color="secondary"
						variant="text"
						href={ALLY_LANDING_URL}
						onClick={onLearnMoreClick}
						target="_blank"
						startIcon={<HelpIcon />}
					>
						{__('Learn more')}
					</Button>
				</Stack>
			</StyledDialogActions>
		</>
	);
};

const StyledDialogContent = styled(DialogContent)`
	padding-bottom: 0;
`;

const StyledVideoBox = styled(Box)`
	width: 100%;
	max-width: 550px;

	position: relative;

	margin: 0 auto ${({ theme }) => theme.spacing(2)};

	img {
		width: 100%;
		height: 100%;

		display: block;

		object-fit: cover;
		object-position: center;
	}

	iframe {
		width: 100%;
		height: 300px;

		display: block;

		border: 0;
	}
`;

const StyledPosterOverlay = styled(Box)`
	position: absolute;
	inset: 0;
	z-index: 1;

	background: ${({ theme }) => theme.palette.common.black};
`;

const StyledContentContainer = styled(Box)`
	margin-top: ${({ theme }) => theme.spacing(1)};

	text-align: center;
`;

const StyledTitle = styled(DialogContentText)`
	margin: 0;

	color: ${({ theme }) => theme.palette.common.black};
	font-size: 32px;
	font-weight: 600;
	line-height: 42px;

	strong {
		color: ${({ theme }) => theme.palette.info.main};
		font-weight: 700;
	}
`;

const StyledSubtitle = styled(DialogContentText)`
	margin: 0;
	margin-bottom: ${({ theme }) => theme.spacing(3)};

	color: ${({ theme }) => theme.palette.text.secondary};
	font-size: 18px;
	font-weight: 400;
	line-height: 24px;
`;

const StyledRepoButton = styled(Button)`
	gap: ${({ theme }) => theme.spacing(1)};

	font-size: 15px;
`;

const StyledDialogActions = styled(DialogActions)`
	justify-content: center;
	align-items: center;
	padding: ${({ theme }) => theme.spacing(1)};

	background-color: #f3f3f4;
`;

const StyledNotOnWordPressText = styled(Typography)`
	display: flex;
	justify-content: center;
	align-items: center;

	margin-top: 16px;

	color: ${({ theme }) => theme.palette.text.secondary};
	font-size: 14px;

	.MuiButton-text {
		font-size: 14px;
		font-weight: 400;
		letter-spacing: 0.15px;
	}
`;

export default GetFreeModalStepInstall;
